import {
  type FC, memo, type ReactElement, useEffect,
} from 'react';
import { type LatLng } from '~/_shared/types/latLng';
import { createUuid } from '~/_shared/utils/createUuid';
import { KeyboardKeys } from '~/_shared/utils/hooks/useKeyPress';
import {
  type MapKeyPressCallback, MapKeyPressPriority, useMapKeyPressContext,
} from '~/_shared/utils/hooks/useMapKeyPressContext';
import { type MapObjectZIndex } from '../mapObject.types';
import { type MapObjectOutlineInstance } from '../mapOutline/mapOutlineModel';
import { MapObjectContextProvider } from '../private/mapObjectContext';
import { type MapShapeInstance } from './mapShapeModel';
import {
  DrawMapShapeContextProvider, useDrawMapShapeContext,
} from './private/drawMapShapeContext';
import { MapShapeContextProvider } from './private/mapShapeContext';
import { MapShapeLines } from './private/mapShapeLines.component';
import { useAddCursorLabel } from './private/useAddCursorLabel';
import { useClickMapShapeDraw } from './private/useClickMapShapeDraw.hook';
import { useFreeformMapShapeDraw } from './private/useFreeformMapShapeDraw.hook';
import { useSnapCursorDot } from './private/useSnapCursorDot.hook';

export enum DrawMapShapeMode {
  Click = 'Click',
  Freeform = 'Freeform',
}

export type ShapeSubmitOptions = {
  FirstOutline: boolean;
  Esc: boolean;
  DoubleClick: boolean;
};

export type ShapeSubmitOption = keyof ShapeSubmitOptions;

const defaultSubmitOptions = { FirstOutline: true, Esc: true, DoubleClick: true };
const identitySnap = (point: LatLng): LatLng => point;

export type DrawMapShapeProps = {
  zIndex: MapObjectZIndex;
  mode?: DrawMapShapeMode;
  submitOptions?: ShapeSubmitOptions;
  onDone?: (how: ShapeSubmitOption) => void;
  onChange?: (shape: MapShapeInstance) => void;
  getCursorLabel?: (position: LatLng) => ({ text: string; angle?: number }) | null | undefined;
  renderLine?: (id: string, start: LatLng, end: LatLng) => ReactElement | null;
  renderOutline?: (id: string, outline: MapObjectOutlineInstance) => ReactElement | null;
  snap?: (point: LatLng) => LatLng | null;
  startingPoint?: LatLng | null;
};

const DrawMapShape: FC<DrawMapShapeProps> = ({
  zIndex,
  mode = DrawMapShapeMode.Click,
  submitOptions = defaultSubmitOptions,
  onDone,
  getCursorLabel,
  renderOutline,
  renderLine,
  snap,
  startingPoint,
}) => {
  const { shape, setShape, manager } = useDrawMapShapeContext();
  const guaranteedSnap = snap ?? identitySnap;

  useEffect(() => { // Start drawing
    const emptyInstance = { id: createUuid(), outlines: [] };

    if (startingPoint) {
      setShape(() => {
        const startingOutline = manager.createOutlineFromLatLng(startingPoint);
        return {
          ...emptyInstance,
          outlines: [startingOutline],
        };
      });
    }
    else {
      setShape(emptyInstance);
    }

    return () => { // Cleanup
      setShape(undefined);
    };
  }, [manager, startingPoint, setShape]);

  useClickMapShapeDraw({
    active: mode === DrawMapShapeMode.Click,
    manager,
    submitOptions,
    onDone,
    snap: guaranteedSnap,
  });

  useFreeformMapShapeDraw({
    active: mode === DrawMapShapeMode.Freeform,
    manager,
    onDone,
    snap: guaranteedSnap,
  });

  const { addMapKeyHandler } = useMapKeyPressContext();

  useEffect(() => { // Finish drawing on ESC
    if (!shape || !onDone || !submitOptions.Esc) {
      return;
    }

    const onEsc: MapKeyPressCallback = (e) => {
      onDone('Esc');
      e.stopPropagation();
    };

    const removeKeyHandler = addMapKeyHandler(KeyboardKeys.Escape, MapKeyPressPriority.FinishDrawing, onEsc);
    return removeKeyHandler;
  }, [shape, submitOptions.Esc, onDone, addMapKeyHandler]);

  // Mouse move while drawing — cursor label and position tracking
  const { cursorPosition: rawCursorPosition } = useAddCursorLabel({
    zIndex,
    getCursorLabel: shape?.outlines.length ? getCursorLabel : undefined,
  });

  const snappedPosition = rawCursorPosition && guaranteedSnap(rawCursorPosition);

  const showSnapCursor = !!snap && !!shape?.outlines.length;

  useSnapCursorDot({
    manager,
    zIndex,
    snappedPosition: showSnapCursor ? (snappedPosition || undefined) : undefined,
  });

  if (!shape) {
    return null;
  }

  const finalPosition = snappedPosition || rawCursorPosition;

  return (
    <MapObjectContextProvider value={{ manager, zIndex }}>
      <MapShapeContextProvider value={{ shape, isPolygon: false }}>
        {renderOutline && shape.outlines.map(outline => renderOutline(outline.id, outline))}
        {renderLine && (
          <>
            <MapShapeLines
              renderLine={renderLine}
            />
            {shape.outlines.length && finalPosition &&
            renderLine(`cursor-line-${shape.id}`, shape.outlines.lastItem, finalPosition)
            }
          </>
        )}
      </MapShapeContextProvider>
    </MapObjectContextProvider>
  );
};

const DrawMapShapeWithProvider: React.FC<DrawMapShapeProps> = (props) => (
  <DrawMapShapeContextProvider onChange={props.onChange}>
    <DrawMapShape {...props} />
  </DrawMapShapeContextProvider>
);

const pureComponent = memo(DrawMapShapeWithProvider);
export { pureComponent as DrawMapShape };
